'use client';

import { Suspense } from 'react';
import AnalyticsTracker from '@/analytics/AnalyticsTracker';
import ContentEngagementClient from '@/analytics/ContentEngagementClient';
import { cleanMetadata } from '@/utils/cleanMetadata';
import { thisHostAndBasePath } from '@/../config';
import ChampionBanner from '@/components/BannerSection/ChampionBanner';
import TableOfContents from '@/components/TableOfContent/TableOfContents';
import ProductCard from '@/components/Cards/ProductCardComponent';
import CarbonAccordion from '@/components/Accordion/Accordion';
import './risingchampions.scss';
import BottomBanner from '@/components/BannerSection/BottomBanner/BottomBanner';
import { Launch } from '@carbon/icons-react';

export default function RisingChampionsPage() {
  const navigationItems = [
    {
      label: 'Turn your passion into impact',
      href: '#turn-your-passion-into-impact',
    },
    {
      label: 'Our Approach to Advocacy Recognition',
      href: '#our-approach-to-advocacy-recognition',
    },
    {
      label: 'Curious about how to best advocate for IBM products?',
      href: '#curious-about-how-to-best-advocate-for-ibm-products',
    },
    {
      label: 'Learn more about the IBM Rising Champions Program',
      href: '#learn-more-about-the-ibm-rising-champions-program',
    },
    {
      label: 'Participate and report your advocacy activities',
      href: '#participate-and-report-your-advocacy-activities',
    },
  ];

  const pageMetaData = cleanMetadata({
    slug: 'champion/risingchampions',
    publish_date: null,
    date_modified: null,
    url: `${thisHostAndBasePath}/community/ibm-rising-champions/`,
    title: 'Rising Champions',
    favourite: '',
    like: '',
    follow: '',
    status: '',
    primary_category: '',
    tags: '',
    strategic_content_area: '',
    authors: '',
    authors_email: '',
    editors: '',
    editors_email: '',
    record_type: 'static-page',
    secondary_tag: '',
  });

  return (
    <div className="champions-page">
      <Suspense fallback={null}>
        <AnalyticsTracker />
      </Suspense>
      <ContentEngagementClient metaData={pageMetaData} />

      {/* HERO */}
      <section className="">
        <ChampionBanner
          title="Start your journey from learner to leader"
          subtitle="IBM celebrates top advocates and community leaders"
          imageUrl="https://dw1.s81c.com/developer-static-pages/open/en/home/images/rising-champions.png"
          imageAlt="IBM Developer Champion - Start your journey from learner to leader"
        />
      </section>

      {/* MAIN LAYOUT */}
      <div className="champions-container">
        {/* SIDEBAR */}
        <aside className="sticky-sidebar">
          <TableOfContents
            navigationItems={navigationItems}
            hideTitle={true}
            defaultActiveFirst={true}
          />
        </aside>

        {/* CONTENT */}
        <main className="champions-content-section rising-champions-content">
          <section className="champions-intro">
            <p className="intro-text">
              An extension of the{' '}
              <a href={`/community/ibm-champions/`} rel="noopener noreferrer">
                IBM Champions program <Launch size={16} aria-label="External link" />
              </a>
              , IBM Rising Champions is a year-round, tiered badging program designed to support and
              amplify the voices of IBM technology advocates among our clients and business
              partners. Earning your Rising Champion status is a step towards accelerating your
              career and unlocking a pathway to becoming a Champion.
            </p>

            {/* <div className="intro-text"> */}
            <h3>A refreshed approach to advocacy recognition</h3>
            <p className="intro-text">
              As the community of advocates has grown, so has the IBM Rising Champions program,
              which has evolved to better reflect how advocacy naturally develops, how impact is
              created, and how meaningful contributions take shape over time.
            </p>
            <p className="intro-text">
              We recognize that advocacy looks different for everyone, and that meaningful impact
              goes beyond simple counts and checklists.
            </p>
            {/* </div> */}
          </section>
          <hr className="section-divider" />
          <section className="content-section">
            <h2 id="turn-your-passion-into-impact">Turn your passion into impact</h2>

            <p>
              You are building a career in a rapidly evolving tech world, and it is your dedication
              to learning and leading that will set you apart. The Rising Champions program helps
              you transform your passion for technology into recognition. Join a network of driven
              professionals committed to continuous growth, sharing knowledge, and making an impact.
            </p>
            <br />
            <br />
            <img
              src="https://dw1.s81c.com/developer-static-pages/open/en/home/images/ibm-champions-turn-your-passion.png"
              alt="Turn your passion into impact"
              className="passion-image"
            />
          </section>
          <hr className="section-divider" />
          <section className="content-section">
            <h2 id="our-approach-to-advocacy-recognition">Our Approach to Advocacy Recognition</h2>

            <p>
              Rising Champions recognizes that advocacy grows over time. While participants begin
              with minimum activity expectations, progression is guided by intent, consistency, and
              impact, not volume alone.
            </p>

            <p>
              As advocates advance from Contributor to Advocate to Influencer, the focus shifts from
              participation to leadership, depth, and sustained ecosystem value. Here's what you
              need to know:
            </p>

            <ul>
              <li>Minimum activity counts apply for evaluation at each badge level.</li>
              <li>Advocacy is evaluated with context, credibility, and consistency in mind.</li>
              <li>Badges are earned through review, not automatically awarded.</li>
              <li>Contributions must extend beyond job responsibilities or business promotion.</li>
            </ul>

            <p>
              IBM Rising Champion badges include the following levels. To review detailed
              requirements, visit these pages:
            </p>

            <ul>
              <li>
                <a href="https://ibm.biz/IBMContributor" target="_blank" rel="noopener noreferrer">
                  IBM Contributor <Launch size={16} aria-label="External link" />
                </a>
              </li>
              <li>
                <a href="https://ibm.biz/IBMAdvocate2026" target="_blank" rel="noopener noreferrer">
                  IBM Advocate <Launch size={16} aria-label="External link" />
                </a>
              </li>
              <li>
                <a href="https://ibm.biz/IBMInfluencer" target="_blank" rel="noopener noreferrer">
                  IBM Influencer <Launch size={16} aria-label="External link" />
                </a>
              </li>
            </ul>

            <p className="note-section">
              <strong>Important note on IBM Z and LinuxONE advocacy:</strong> If your activity is
              primarily related to IBM Z and LinuxONE, you may want to begin with the{' '}
              <a href="https://community.ibm.com/zsystems/advocacy/" target="_blank">
                Z and LinuxONE Advocacy Hub <Launch size={16} aria-label="External link" />
              </a>
              . Please note that activities used for earning the IBM Z and LinuxONE community badges
              will not be double-counted for badges in the IBM Rising Champions Advocacy program;
              however, you may earn the Rising Champions advocacy badges for activity that is not
              used in earning the IBM Z and LinuxONE badges. If you are evaluated for IBM Champion
              status, the selection committee may review all advocacy activity and consider any
              badges earned across each program.
            </p>
          </section>
          <hr className="section-divider" />
          <section className="content-section">
            <h2 id="curious-about-how-to-best-advocate-for-ibm-products">
              Curious about how to best advocate for IBM products?
            </h2>

            <p>
              There are plenty of advocacy activities for everyone! Check out the kind of advocacy
              that fits you best, and get ready to start your journey! Here are a few examples:
            </p>
            <br />
            <br />
            <CarbonAccordion
              singleOpen={true}
              items={[
                {
                  key: 'product-reviews',
                  title: 'Represent the voice of the customer by writing product reviews',
                  openByDefault: true,
                  content: (
                    <>
                      <div className="review-cards-grid">
                        <ProductCard
                          title="G2 Software"
                          imageUrl="https://dw1.s81c.com/developer-static-pages/open/en/home/images/g2-software-card.png"
                          imageAlt="G2 Software"
                        />

                        <ProductCard
                          title="Gartner Peer Insights"
                          imageUrl="https://dw1.s81c.com/developer-static-pages/open/en/home/images/gartner-peer-insights-card.png"
                          imageAlt="Gartner Peer Insights"
                        />

                        <ProductCard
                          title="TrustRadius"
                          imageUrl="https://dw1.s81c.com/developer-static-pages/open/en/home/images/trustradius-card.png"
                          imageAlt="TrustRadius"
                        />
                      </div>

                      <p>
                        One of the fastest and easiest ways for you to earn a Rising Champions badge
                        is by providing product feedback.
                        {/* It only takes two product reviews to earn
                        your IBM Contributor 2025 badge! Simply create an account on any of the{' '}
                        <a
                          href="https://www.gartner.com/peer-insights/home"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Gartner Peer Insights <Launch size={16} aria-label="External link" />
                        </a>
                        ,{' '}
                        <a
                          href="https://www.g2.com/search?utf8=%E2%9C%93&query=IBM&product_id=4350&product_id=27540&product_id=1254&product_id=2773&product_id=3640"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          G2 <Launch size={16} aria-label="External link" />
                        </a>
                        , or{' '}
                        <a
                          href="https://www.trustradius.com/products?q=IBM"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          TrustRadius <Launch size={16} aria-label="External link" />
                        </a>{' '}
                        review platforms, and share your thoughts on IBM products. */}
                      </p>
                    </>
                  ),
                },
                {
                  key: 'ibm-community',
                  title: 'Post and engage on the IBM Community',
                  content: (
                    <ul>
                      <li>
                        Create original technical content on the IBM Community in the form of a
                        blog, a forum comment or answer, or documentation (must be a minimum of 150
                        words).
                      </li>
                    </ul>
                  ),
                },
                {
                  key: 'technical-communities',
                  title: 'Support and engage with other relevant technical communities',
                  content: (
                    <ul>
                      <li>
                        Answer questions, build community discourse, or moderate a forum across
                        social or community properties such as LinkedIn, Discord, or StackOverflow,
                        etc.
                      </li>
                    </ul>
                  ),
                },
                {
                  key: 'digital-content',
                  title: 'Create and publish digital content across multiple platforms',
                  content: (
                    <ul>
                      <li>
                        Create (or translate) and publish content about IBM solutions (e.g., blog
                        posts, articles, videos, podcasts, case studies, white papers, and books).
                      </li>
                    </ul>
                  ),
                },
                {
                  key: 'user-groups',
                  title: 'Start, volunteer for, or manage an IBM User Group or meetup',
                  content: (
                    <ul>
                      <li>
                        Lead, volunteer for, or manage{' '}
                        <a
                          href="https://www.ibm.com/community/user-groups/"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          user groups <Launch size={16} aria-label="External link" />
                        </a>{' '}
                        or meetups that focus on IBM technologies.
                      </li>
                    </ul>
                  ),
                },
                {
                  key: 'conferences',
                  title: 'Present and co-present at conferences and user group events',
                  content: (
                    <ul>
                      <li>
                        Present a session on IBM solutions at user groups, webinars, podcasts, or
                        industry events such as IBM Think, IBM TechXchange, or other conferences and
                        events.
                      </li>
                    </ul>
                  ),
                },
                {
                  key: 'product-enhancement',
                  title: 'Bring your great product enhancement to ideas.ibm.com',
                  content: (
                    <ul>
                      <li>
                        Submit your suggestions to{' '}
                        <a href="https://ideas.ibm.com/" target="_blank" rel="noopener noreferrer">
                          ideas.ibm.com <Launch size={16} aria-label="External link" />
                        </a>{' '}
                        to help improve IBM products and shape future updates and features.
                      </li>
                    </ul>
                  ),
                },
                {
                  key: 'feedback-councils',
                  title:
                    'Provide feedback and product ideas through councils, sponsor-user opportunities, and boards',
                  content: (
                    <ul>
                      <li>
                        Provide product feedback and/or technical contributions by participating in
                        councils, surveys, and alpha/beta testing opportunities.
                      </li>
                      <li>
                        Join a customer advisory board to share feedback directly with IBM.
                        Collaborate with peers and influence product direction.
                      </li>
                    </ul>
                  ),
                },
                {
                  key: 'sales-support',
                  title: 'Act as an experienced user for sales, press, and analyst support',
                  content: (
                    <ul>
                      <li>
                        Provide quotes, testimonials, and references to support sales and marketing.
                      </li>
                    </ul>
                  ),
                },
                {
                  key: 'mentoring',
                  title: 'Mentor or coach other technology professionals',
                  content: (
                    <ul>
                      <li>
                        Support others by mentoring or coaching in the tech community. Share your
                        knowledge and help others grow.
                      </li>
                    </ul>
                  ),
                },
                {
                  key: 'code-contributions',
                  title: 'Contribute your code or other technical contributions',
                  content: (
                    <ul>
                      <li>
                        Contribute code to or provide other support to an IBM-related open-source
                        projects, such as Hyperledger Fabric, Open Mainframe, or a Call for Code
                        project.
                      </li>
                      <li>Support hackathons by mentoring or judging contributions.</li>
                      <li>
                        Create and teach an IBM technology-related course or participate in an
                        academic research project about an IBM solution.
                      </li>
                      <li>Act as a subject matter expert to create IBM certification exams.</li>
                    </ul>
                  ),
                },
              ]}
            />
            <br />
            <p>
              Are you doing an activity that you believe should be considered advocacy? Just reach
              out to our team to discuss at{' '}
              <a href="mailto:ibmchampions@ibm.com">ibmchampions@ibm.com</a>, as this list is not
              exhaustive.
            </p>
          </section>
          <hr className="section-divider" />
          <section className="content-section">
            <h2 id="learn-more-about-the-ibm-rising-champions-program">
              Learn more about the IBM Rising Champions Program
            </h2>

            <p>
              <a href="https://ibm.biz/RisingChamp2" target="_blank" rel="noopener noreferrer">
                Read our detailed introduction <Launch size={16} aria-label="External link" />
              </a>{' '}
              to understand the IBM Rising Champions program, how evaluation works, and what success
              can look like at each level.
            </p>
          </section>
          <hr className="section-divider" />
          <section className="content-section">
            <h2 id="participate-and-report-your-advocacy-activities">
              Participate and report your advocacy activities
            </h2>

            <p>
              If you are new to the IBM Rising Champions, get started by completing the{' '}
              <a
                href="https://ibm.biz/apply_AdvocacyBadge"
                target="_blank"
                rel="noopener noreferrer"
              >
                Candidate Application <Launch size={16} aria-label="External link" />
              </a>
              . Once you've applied, you (along with other Rising Champions and Champions) will
              report advocacy activities using the{' '}
              <a href="https://ibm.biz/champ-report" target="_blank" rel="noopener noreferrer">
                Activity Reporting Form <Launch size={16} aria-label="External link" />
              </a>
              . The committee requires credible evidence and context for thoughtful evaluation
              across all program levels. For more information and guidance about what to report and
              what we evaluate, please visit the{' '}
              <a
                href="https://community.ibm.com/community/user/blogs/kathryn-schwab1/2026/05/22/understanding-activities-advocacy-comes-in-many-fo"
                target="_blank"
                rel="noopener noreferrer"
              >
                Understanding Activities article <Launch size={16} aria-label="External link" />
              </a>
              .
            </p>
          </section>
        </main>
      </div>
      <BottomBanner
        title="Start your advocacy journey here"
        subHeader="Build your voice, grow your network, and gain recognition as you develop your expertise with IBM technologies."
        ctas={[
          {
            label: 'New? Start your Rising Champion application',
            href: 'https://ibm.biz/apply_AdvocacyBadge',
          },
          {
            label: 'Part of the program? Report your activities & earn badges',
            href: 'https://ibm.biz/champ-report',
          },
        ]}
        learnMoreText="Learn more"
        learnMoreLink="https://community.ibm.com/community/user/blogs/kathryn-schwab1/2026/05/22/introducing-the-next-evolution-of-the-ibm-rising-c"
        backgroundColor="#f4f4f4"
      />
    </div>
  );
}
